import React, { useState, useEffect } from 'react';
import { User } from 'lucide-react';

import LoginView from './components/views/LoginView';
import StudentHomeView from './components/views/StudentHomeView';
import StudentProfileView from './components/views/StudentProfileView';
import PhotoAlbumPlayer from './components/ui/PhotoAlbumPlayer';
import IdentityUpgradeModal from './components/ui/IdentityUpgradeModal';
import InitialLoading from './components/common/InitialLoading';
import SearchOverlay from './components/layout/SearchOverlay';
import ToastContainer from './components/ui/ToastContainer';

import usePermissionStore from './stores/permissionStore';
import { useToast } from './hooks/useToast';
import authService from './services/authService';
import apiClient from './services/apiClient';

export default function App() {
  const { isLoggedIn, setLoginInfo, user } = usePermissionStore();
  const toast = useToast();

  const [currentView, setCurrentView] = useState('loading');
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const [isInitializing, setIsInitializing] = useState(true);

  // 初始化时检查 URL 中的访问令牌
  useEffect(() => {
    const handleAccessCheck = async () => {
      // 仅支持路径模式 /token/:token
      const path = window.location.pathname;
      let token = null;
      
      if (path.startsWith('/token/')) {
        token = path.split('/token/')[1];
      }
      
      if (token) {
        console.log('检测到访问令牌:', token);
        try {
          // 发现新令牌，先清除旧身份，防止 Cookie/Token 冲突
          apiClient.clearAuth();
          usePermissionStore.getState().logout();
          
          setIsInitializing(true);
          // 调用 tokenLogin 进行登录
          const response = await authService.tokenLogin(token);
          console.log('令牌登录成功:', response);
          
          localStorage.setItem('access_token', token);
          
          // 更新权限 Store
          if (response.user) {
            setLoginInfo(response.user, response.access_token);
          }
          
          setCurrentView('student_home');
          // 清除 URL 并跳转到根路径，保持干净
          window.history.replaceState({}, '', '/');
        } catch (error) {
          console.error('令牌登录失败:', error);
          toast.error('访问令牌无效或已过期');
          setCurrentView('login');
        }
      } else {
        // 如果没有新token，检查本地是否已有
        const localToken = localStorage.getItem('access_token');
        if (localToken && isLoggedIn) {
          // 本地有令牌且 permissionStore 标记为已登录，直接恢复
          setCurrentView('student_home');
        } else if (localToken && !isLoggedIn) {
          // 本地有令牌但未登录（可能是过期残留），尝试重新验证
          try {
            const response = await authService.tokenLogin(localToken);
            console.log('令牌重新验证成功:', response);
            if (response.user) {
              setLoginInfo(response.user, response.access_token);
            }
            setCurrentView('student_home');
          } catch (err) {
            // 令牌无效或过期，清除并显示登录页
            console.warn('本地令牌已失效，清除并跳转登录页:', err.message);
            localStorage.removeItem('access_token');
            apiClient.clearAuth();
            setCurrentView('login');
          }
        } else {
          setCurrentView(isLoggedIn ? 'student_home' : 'login');
        }
      }
      setIsInitializing(false);
    };
    
    handleAccessCheck();
  }, [setLoginInfo]); // 移除 isLoggedIn 依赖，防止无限循环，仅初始化执行一次

  // 状态同步：当登录状态发生变化且不在初始化时，同步视图
  useEffect(() => {
    if (isInitializing) return;

    if (isLoggedIn || user?.is_token_access) {
      if (currentView === 'login' || currentView === 'loading') {
        setCurrentView('student_home');
      }
    } else if (currentView !== 'login') {
      setCurrentView('login');
    }
  }, [isLoggedIn, user?.is_token_access, isInitializing, currentView]);

  // 移除旧的身份同步 Effect，因为现在直接通过 user.is_token_access 判断

  // 订阅登出事件
  useEffect(() => {
    return usePermissionStore.subscribe((state, prevState) => {
      if (prevState.isLoggedIn && !state.isLoggedIn) {
        setCurrentView('login');
        setIsProfileOpen(false);
      }
    });
  }, []);

  const handleLogin = () => {
    setCurrentView('student_home');
  };

  const renderView = () => {
    switch (currentView) {
      case 'login':
        return <LoginView onLogin={handleLogin} />;

      case 'student_home':
        return (
          <StudentHomeView
            searchParams={searchParams}
            searchTrigger={searchTrigger}
            onSearch={() => setIsSearchOpen(true)}
          />
        );

      case 'student_album':
        return (
          <PhotoAlbumPlayer
            onClose={() => setCurrentView('student_home')}
          />
        );
      case 'loading':
        return <InitialLoading />;

      default:
        return <LoginView onLogin={handleLogin} />;
    }
  };

  const showFloatBtn = (isLoggedIn || user?.is_token_access) && currentView !== 'login';

  return (
    <div className="h-screen bg-white font-sans flex flex-col overflow-hidden">
      <SearchOverlay
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        searchContext="record"
        onSearch={(params) => {
          setSearchParams(params);
          setSearchTrigger((p) => p + 1);
        }}
      />
      <div className="flex-1 overflow-hidden">{renderView()}</div>

      {/* 浮动「我的」按钮 */}
      {showFloatBtn && (
        <button
          onClick={() => {
            if (user?.is_token_access) {
              setIsUpgradeModalOpen(true);
            } else {
              setIsProfileOpen(true);
            }
          }}
          aria-label="个人中心"
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '20px',
            zIndex: 60,
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3182CE 0%, #4299E1 100%)',
            boxShadow: '0 4px 16px rgba(49,130,206,0.45)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
            border: 'none',
            cursor: 'pointer',
            transition: 'transform 0.18s ease, box-shadow 0.18s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(49,130,206,0.55)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(49,130,206,0.45)'; }}
          onTouchStart={e => { e.currentTarget.style.transform = 'scale(0.95)'; }}
          onTouchEnd={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          <User size={20} color="#fff" strokeWidth={2.2} />
          <span style={{ color: '#fff', fontSize: '9px', fontWeight: 700, letterSpacing: '0.02em', lineHeight: 1 }}>我的</span>
        </button>
      )}

      {/* 个人中心 Sheet（全屏滑入层） */}
      {isProfileOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 70,
            display: 'flex', flexDirection: 'column',
          }}
        >
          {/* 半透明遮罩 */}
          <div
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(2px)' }}
            onClick={() => setIsProfileOpen(false)}
          />
          {/* Sheet 主体 */}
          <div
            style={{
              position: 'relative',
              marginTop: 'auto',
              width: '100%',
              maxHeight: '92vh',
              borderRadius: '24px 24px 0 0',
              overflow: 'hidden',
              background: '#F7FAFC',
              display: 'flex',
              flexDirection: 'column',
              animation: 'sheetSlideUp 0.32s cubic-bezier(0.32,1,0.56,1) both',
            }}
          >
            <style>{`
              @keyframes sheetSlideUp {
                from { transform: translateY(100%); opacity: 0.6; }
                to   { transform: translateY(0);    opacity: 1;   }
              }
            `}</style>
            {/* 拖拽指示条 */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '12px', paddingBottom: '4px', flexShrink: 0 }}>
              <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: '#CBD5E0' }} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <StudentProfileView onClose={() => setIsProfileOpen(false)} />
            </div>
          </div>
        </div>
      )}

      {/* 身份升级弹窗 */}
      <IdentityUpgradeModal 
        isOpen={isUpgradeModalOpen} 
        onClose={() => setIsUpgradeModalOpen(false)}
        onSuccess={() => {
          setIsUpgradeModalOpen(false);
          setIsProfileOpen(true); // 验证成功后自动打开个人中心
          toast.success('身份验证成功');
        }}
      />

      <ToastContainer />
    </div>
  );
}
